import PropTypes from 'prop-types';
import React from 'react';

import 'react-big-calendar/lib/css/react-big-calendar.css';

import { Button, Card, Container, Flex, Spinner, Text, ThemeProvider } from 'theme-ui';

import {
  AppointmentTime,
  HorizontalMenu,
  Layout,
  RefreshButton,
  SEO,
  VisitPreview,
} from '../../../components';
import { STRINGS as COMMON_STRINGS, VISIT_STATUSES } from '../../../constants';
import { STRINGS } from '../../../constants/workshop';
import theme from '../../../theme';
import styles from './VisitsScreen.style';

const VisitsScreen = (props) => {
  const {
    status,
    visitsForDate,
    visitsOngoing,
    visitsUnscheduled,
    loadMoreVisitsForDate,
    loadMoreVisitsOngoing,
    loadMoreVisitsUnscheduled,
    setSelectedMenu,
    selectedMenu,
    isLoading,
    isLoadingMoreData,
    limit,
    // eslint-disable-next-line no-unused-vars
    isRefreshing,
    onRefresh,
    lastVisitRef,
    error,
  } = props;

  const renderAppointmentTime = (scheduledTime, label) => {
    if (scheduledTime) {
      return <AppointmentTime scheduledTime={scheduledTime} label={label} />;
    }
    return null;
  };

  const renderVisit = ({ item, index, isLast }) => {
    return (
      <div ref={isLast ? lastVisitRef : null} key={item?.id}>
        <VisitPreview key={item?.id} item={item} testID={`visit${index}`}>
          {item?.scheduledTime &&
            item.status !== VISIT_STATUSES.REJECTED &&
            renderAppointmentTime(item.scheduledTime, COMMON_STRINGS.visit.visitTime)}
          {item.status === VISIT_STATUSES.PROPOSED_TIME && (
            <>
              {renderAppointmentTime(
                item?.timePropositions?.firstProposition,
                COMMON_STRINGS.visit.firstVisitProposition,
              )}
              {renderAppointmentTime(
                item?.timePropositions?.secondProposition,
                COMMON_STRINGS.visit.secondVisitProposition,
              )}
            </>
          )}
          {item.status === VISIT_STATUSES.PROPOSED_NEW_TIME && (
            <>
              {renderAppointmentTime(
                item?.alternativeTimePropositions?.firstProposition,
                COMMON_STRINGS.visit.firstVisitProposition,
              )}
              {renderAppointmentTime(
                item?.alternativeTimePropositions?.secondProposition,
                COMMON_STRINGS.visit.secondVisitProposition,
              )}
            </>
          )}
          {item.status === VISIT_STATUSES.REJECTED && (
            <>
              {renderAppointmentTime(
                item?.workshopRejectedVisit?.firstProposition,
                COMMON_STRINGS.visit.firstVisitProposition,
              )}
              {renderAppointmentTime(
                item?.workshopRejectedVisit?.secondProposition,
                COMMON_STRINGS.visit.secondVisitProposition,
              )}
            </>
          )}
        </VisitPreview>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const renderButtonWhenNoVisits = (noVisitsText) => {
    return (
      <Flex sx={styles.buttonContainer}>
        <Flex sx={styles.refreshButtonContainer}>
          <Button sx={styles.refreshVisitsButton} onClick={onRefresh}>
            <Text sx={styles.refreshVisitsButtonText}>{STRINGS.visits.refreshVisitsText}</Text>
          </Button>
        </Flex>
        <Text sx={styles.noVisitsText}>{noVisitsText}</Text>
      </Flex>
    );
  };

  const renderButtonWhenError = (errorText) => {
    return (
      <Flex sx={styles.buttonContainer}>
        <Flex sx={styles.refreshButtonContainer}>
          <Button sx={styles.refreshVisitsButton} onClick={onRefresh}>
            <Text sx={styles.refreshVisitsButtonText}>{STRINGS.visits.refreshVisitsText}</Text>
          </Button>
        </Flex>
        <Text sx={styles.error}>{errorText}</Text>
      </Flex>
    );
  };

  const renderLoadMoreButton = (data, loadingFunction) => {
    if (isLoadingMoreData) {
      return (
        <Flex sx={styles.spinnerContainer}>
          <Spinner size={60} />
        </Flex>
      );
    }

    if (
      typeof data?.count === 'number' &&
      data?.count <= (data?.offset ?? 0) + (data?.limit ?? limit)
    ) {
      return null;
    }
    return (
      <Flex sx={styles.spinnerContainer}>
        <Button onClick={() => loadingFunction()} sx={styles.loadMoreVisitsIcon}>
          <Text sx={styles.loadMoreVisitsIconText}>{STRINGS.visits.loadMoreVisitsText}</Text>
        </Button>
      </Flex>
    );
  };

  const renderVisits = (visits, noVisitsText, loadMoreFunction) => {
    if (!isLoading) {
      if (error) {
        return renderButtonWhenError(error);
      }
      if (visits?.visits && visits?.visits.length !== 0) {
        return (
          <Flex sx={styles.containerFlatList}>
            <Flex sx={styles.visitsScroll} id="scrollBox">
              {visits?.visits.map((item, index) => {
                return renderVisit({ item, index, isLast: visits?.visits.length === index + 1 });
              })}
              {renderLoadMoreButton(visits, loadMoreFunction)}
            </Flex>
          </Flex>
        );
      }
      return renderButtonWhenNoVisits(noVisitsText);
    }
    return (
      <>
        <Flex sx={styles.manualRefreshButton}>
          <Button sx={styles.refreshVisitsButton} onClick={onRefresh}>
            <Text sx={styles.refreshVisitsButtonText}>{STRINGS.visits.refreshVisitsText}</Text>
          </Button>
        </Flex>
        <Flex sx={styles.spinnerContainer}>
          <Spinner size={60} />
        </Flex>
      </>
    );
  };

  const VisitsPanel = () => {
    return (
      <Card variant="visitsCard" sx={styles.card}>
        <Flex sx={styles.menuContainer}>
          <HorizontalMenu
            labels={[
              COMMON_STRINGS.visit.todaysVisits,
              COMMON_STRINGS.visit.ongoingVisits,
              COMMON_STRINGS.visit.newVisits,
            ]}
            setSelectedMenu={setSelectedMenu}
            selectedMenu={selectedMenu}
          >
            {renderVisits(
              visitsForDate,
              STRINGS.visits.noVisitsForDateToShowText,
              loadMoreVisitsForDate,
            )}
            {renderVisits(
              visitsOngoing,
              STRINGS.visits.noVisitsOngoingToShowText,
              loadMoreVisitsOngoing,
            )}
            {renderVisits(
              visitsUnscheduled,
              STRINGS.visits.noVisitsUnscheduledToShowText,
              loadMoreVisitsUnscheduled,
            )}
          </HorizontalMenu>
        </Flex>
      </Card>
    );
  };

  if (status === 'authenticated') {
    return (
      <ThemeProvider theme={theme}>
        <Layout>
          <SEO title={COMMON_STRINGS.seo.visits} />
          <Container sx={styles.container}>
            <RefreshButton isRefreshing={isRefreshing} onRefresh={onRefresh} />
            <VisitsPanel />
          </Container>
        </Layout>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <SEO title={COMMON_STRINGS.seo.loading} />
        <Container sx={styles.container}>
          <Flex sx={styles.spinnerContainer}>
            <Spinner size={60} />
          </Flex>
        </Container>
      </Layout>
    </ThemeProvider>
  );
};

VisitsScreen.propTypes = {
  visitsForDate: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  visitsOngoing: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  visitsUnscheduled: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  loadMoreVisitsForDate: PropTypes.func.isRequired,
  loadMoreVisitsOngoing: PropTypes.func.isRequired,
  loadMoreVisitsUnscheduled: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMoreData: PropTypes.bool.isRequired,
  limit: PropTypes.number.isRequired,
  isRefreshing: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
  error: PropTypes.string,
};

VisitsScreen.defaultProps = {
  visitsForDate: undefined,
  visitsOngoing: undefined,
  visitsUnscheduled: undefined,
  error: undefined,
};

export default VisitsScreen;
